/**
 * Bill API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  Bill,
  BillListParams,
  BillListResponse,
  BillSummaryStats,
  CreateBillRequest,
  UpdateBillRequest,
  UpdateBillStatusRequest,
  BillApprovalRequest,
  RecordPaymentRequest,
  MarkAsPaidRequest,
  BulkBillStatusUpdateRequest,
  CreateBillLineItemRequest,
  UpdateBillLineItemRequest,
} from '../types/bill';

/**
 * Bill API Endpoints
 */
export const billApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // BILL CRUD
    // ============================================================================

    // Get all bills for a project
    getBills: builder.query<
      ApiResponse<BillListResponse>,
      { projectId: string; params?: BillListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/bills`,
        params,
      }),
      providesTags: ['Bill'],
    }),

    // Get single bill
    getBill: builder.query<ApiResponse<Bill>, { projectId: string; billId: string }>({
      query: ({ projectId, billId }) => ({
        url: `/projects/${projectId}/bills/${billId}`,
      }),
      providesTags: (_result, _error, { billId }) => [{ type: 'Bill', id: billId }],
    }),

    // Create bill
    createBill: builder.mutation<ApiResponse<Bill>, { projectId: string; data: CreateBillRequest }>(
      {
        query: ({ projectId, data }) => ({
          url: `/projects/${projectId}/bills`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Bill'],
      }
    ),

    // Create bill from Purchase Order
    createBillFromPO: builder.mutation<
      ApiResponse<Bill>,
      { projectId: string; purchaseOrderId: string }
    >({
      query: ({ projectId, purchaseOrderId }) => ({
        url: `/projects/${projectId}/bills/from-po/${purchaseOrderId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Bill', 'PurchaseOrder'],
    }),

    // Update bill
    updateBill: builder.mutation<
      ApiResponse<Bill>,
      { projectId: string; billId: string; data: UpdateBillRequest }
    >({
      query: ({ projectId, billId, data }) => ({
        url: `/projects/${projectId}/bills/${billId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { billId }) => ['Bill', { type: 'Bill', id: billId }],
    }),

    // Delete bill
    deleteBill: builder.mutation<ApiResponse<void>, { projectId: string; billId: string }>({
      query: ({ projectId, billId }) => ({
        url: `/projects/${projectId}/bills/${billId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Bill'],
    }),

    // ============================================================================
    // STATUS MANAGEMENT
    // ============================================================================

    // Update status
    updateBillStatus: builder.mutation<
      ApiResponse<Bill>,
      { projectId: string; billId: string; data: UpdateBillStatusRequest }
    >({
      query: ({ projectId, billId, data }) => ({
        url: `/projects/${projectId}/bills/${billId}/status`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { billId }) => ['Bill', { type: 'Bill', id: billId }],
    }),

    // Submit for approval
    submitBillForApproval: builder.mutation<
      ApiResponse<Bill>,
      { projectId: string; billId: string }
    >({
      query: ({ projectId, billId }) => ({
        url: `/projects/${projectId}/bills/${billId}/submit`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { billId }) => ['Bill', { type: 'Bill', id: billId }],
    }),

    // Approve or reject
    processBillApproval: builder.mutation<
      ApiResponse<Bill>,
      { projectId: string; billId: string; data: BillApprovalRequest }
    >({
      query: ({ projectId, billId, data }) => ({
        url: `/projects/${projectId}/bills/${billId}/approve`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { billId }) => ['Bill', { type: 'Bill', id: billId }],
    }),

    // ============================================================================
    // PAYMENT OPERATIONS
    // ============================================================================

    // Record payment
    recordBillPayment: builder.mutation<
      ApiResponse<Bill>,
      { projectId: string; billId: string; data: RecordPaymentRequest }
    >({
      query: ({ projectId, billId, data }) => ({
        url: `/projects/${projectId}/bills/${billId}/payment`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { billId }) => ['Bill', { type: 'Bill', id: billId }],
    }),

    // Mark as fully paid
    markBillAsPaid: builder.mutation<
      ApiResponse<Bill>,
      { projectId: string; billId: string; data?: MarkAsPaidRequest }
    >({
      query: ({ projectId, billId, data }) => ({
        url: `/projects/${projectId}/bills/${billId}/mark-paid`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { billId }) => ['Bill', { type: 'Bill', id: billId }],
    }),

    // ============================================================================
    // BULK OPERATIONS
    // ============================================================================

    // Bulk status update
    bulkUpdateBillStatus: builder.mutation<
      ApiResponse<{ updated: number; failed: number }>,
      { projectId: string; data: BulkBillStatusUpdateRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/bills/bulk-status`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Bill'],
    }),

    // ============================================================================
    // STATISTICS & REPORTING
    // ============================================================================

    // Get summary stats
    getBillStats: builder.query<ApiResponse<BillSummaryStats>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/bills/stats`,
      }),
      providesTags: ['Bill'],
    }),

    // ============================================================================
    // LINE ITEM OPERATIONS
    // ============================================================================

    // Add line item
    addBillLineItem: builder.mutation<
      ApiResponse<Bill>,
      { projectId: string; billId: string; data: CreateBillLineItemRequest }
    >({
      query: ({ projectId, billId, data }) => ({
        url: `/projects/${projectId}/bills/${billId}/line-items`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { billId }) => ['Bill', { type: 'Bill', id: billId }],
    }),

    // Update line item
    updateBillLineItem: builder.mutation<
      ApiResponse<Bill>,
      {
        projectId: string;
        billId: string;
        lineItemId: string;
        data: UpdateBillLineItemRequest;
      }
    >({
      query: ({ projectId, billId, lineItemId, data }) => ({
        url: `/projects/${projectId}/bills/${billId}/line-items/${lineItemId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { billId }) => ['Bill', { type: 'Bill', id: billId }],
    }),

    // Remove line item
    removeBillLineItem: builder.mutation<
      ApiResponse<Bill>,
      { projectId: string; billId: string; lineItemId: string }
    >({
      query: ({ projectId, billId, lineItemId }) => ({
        url: `/projects/${projectId}/bills/${billId}/line-items/${lineItemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { billId }) => ['Bill', { type: 'Bill', id: billId }],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // CRUD
  useGetBillsQuery,
  useGetBillQuery,
  useCreateBillMutation,
  useCreateBillFromPOMutation,
  useUpdateBillMutation,
  useDeleteBillMutation,
  // Status Management
  useUpdateBillStatusMutation,
  useSubmitBillForApprovalMutation,
  useProcessBillApprovalMutation,
  // Payment Operations
  useRecordBillPaymentMutation,
  useMarkBillAsPaidMutation,
  // Bulk Operations
  useBulkUpdateBillStatusMutation,
  // Statistics
  useGetBillStatsQuery,
  // Line Items
  useAddBillLineItemMutation,
  useUpdateBillLineItemMutation,
  useRemoveBillLineItemMutation,
} = billApi;

/**
 * Re-export types for convenience
 */
export type { BillListParams } from '../types/bill';
